import JsBarcode from "jsbarcode";
import moment from "moment";
import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";

// Common
import { getLogoReportNResize } from "../common/CommonInterface";
import { loadImage, resizeLogo } from "../common/CompanyLogoForm";
import { formatDatetime } from "react-lib/utils/dateUtils";

import CONFIG from "config/config";

export type FormMeLabOrderQueueProps = {
  oaLogoBase64?: string;
  lab_type?: string;
  data: LabOrderData;
  hn_data: HNData;
  lab_data: LabData;
  username?: string;
  encounterNumber?: string;
};

export type LabOrderData = {
  full_access_number?: string;
  lab_speciality?: number;
  highest_urgency?: string;
  hn?: string;
  patient_name?: string;
  date?: string;
  doctor_name?: string;
  division_name?: string;
  note?: string;
  gender?: string;
};

export type HNData = {
  gender_name?: string;
  birthdate?: string;
  full_age?: string;
  coverage?: string;
};

export type LabData = {
  specimen_containers?: Record<string, any>[];
};

const LOGO_HEIGHT_MAP: Record<any, number> = {
  RAMASRI: 40,
};

const textToBase64BarcodeBlob = (text: string) => {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, text, { displayValue: false });
  return canvas.toDataURL("image/png");
};

export const FormMeLabOrderQueue = async (props: FormMeLabOrderQueueProps) => {
  console.log("createPDFLabRequest props", props);

  const printDate = formatDatetime(moment());

  const oaLogoBase64 = props?.oaLogoBase64 || "";

  const logoHeight = LOGO_HEIGHT_MAP[CONFIG.COMPANY] ?? 30;

  const { images } = await CompanyLogoForm({
    font: "THSarabunNew",
    height: logoHeight,
    form: "FormLabRequest",
  });

  const logo = await getLogoReportNResize(logoHeight, 1, "FormLabRequest");

  let oaLogoWidth = logoHeight;

  if (oaLogoBase64) {
    const img = await loadImage(oaLogoBase64);
    const resized = resizeLogo(img.width, img.height, logoHeight);

    oaLogoWidth = resized.width;
  }

  const truncateTextBasedWidth = (text: string, maxWidth: number, charWidth: number) => {
    let maxChars = Math.floor(maxWidth / charWidth);

    if (text.length > maxChars) {
      return text.substring(0, maxChars - 3) + "...";
    }

    return text;
  };

  // BLOOD_COMPONENT = 105, "Blood Component"
  const isBloodComponent = props?.data?.lab_speciality === 105;

  let logoColumn = null;

  if (!CONFIG.HIDE_COMPANY_LOGO_PRINT && oaLogoBase64) {
    logoColumn = {
      image: "oaLogo",
      height: logoHeight,
      width: oaLogoWidth,
    };
  }

  const printUrgency = (highest_urgency?: string) => {
    if (highest_urgency === "STAT") {
      return {
        text: "STAT",
        style: "fieldHead",
        color: "red",
      };
    } else if (highest_urgency === "FAST_STACK") {
      return {
        text: "FAST_STACK",
        style: "fieldHead",
        color: "red",
      };
    } else {
      return { text: " " };
    }
  };

  const labOrderList = props?.lab_data?.specimen_containers?.map((item: any) => {
    const printLabcode = (lab_code: any) => {
      let input = lab_code?.split(",");
      var labOutput = "";
      for (let i = 0; i < input.length; i++) {
        labOutput += input[i] + "\n";
      }

      return labOutput;
    };

    const printLabtest = (lab_test: any) => {
      let input = lab_test?.split("-");
      return input;
    };

    return [
      { text: item?.specimen_name || "", style: "fieldValues", margin: [10, 0, 0, 0] },
      { text: printLabcode(item?.lab_code || ""), style: "fieldValues" },
      { text: item?.labResultName ? (item?.labResultName || []).join(`\n`) : printLabtest(item?.all_lab_test || ""), style: "fieldValues" },
    ];
  }) ?? [{}, {}, {}];

  return {
    defaultStyle: {
      font: "THSarabunNew",
    },
    pageMargins: [8, 167, 0, 90],
    pageSize: "A4",
    styles: {
      fieldHead: {
        bold: true,
        fontSize: 18,
      },
      fieldIDHead: {
        bold: true,
        fontSize: 16,
      },
      fieldOPHead: {
        bold: true,
        fontSize: 18,
      },
      fieldDataBold: {
        bold: true,
        fontSize: 14,
      },
      fieldValues: {
        fontSize: 14,
      },
      fieldFooter: {
        fontSize: 12,
      },
      tableHeader: {
        fillColor: "white",
      },
      tableNormal: {
        margin: [0, 5, 0, 5],
      },

      tableExample: {
        margin: [0, 0, 0, 0],
      },
    },
    header: {
      margin: [10, 10, 10, 0],
      stack: [
        {
          columns: [
            logoColumn,
            {
              text: `${props?.lab_type || ""}: ${props?.data?.full_access_number || ""}`,
              style: "fieldIDHead",
              alignment: "right",
            },
          ],
        },
        {
          text: [
            {
              text: isBloodComponent ? "ใบจองเลือด" : "ใบขอตรวจแลป (Lab Request)",
              style: "fieldHead",
              alignment: "center",
            },
          ],
        },
        {
          margin: [5, 0, 5, 0],
          table: {
            widths: ["*", "*"],
            body: [
              [
                {
                  text: [
                    { text: "Member ID : ", style: "fieldDataBold" },
                    { text: props?.data?.hn || "", style: "fieldValues" },
                  ],
                  lineHeight: 0.8,
                  border: [false, false, false, false],
                },
                {
                  text: [
                    { text: "หมายเลขรับบริการ (VN) : ", style: "fieldDataBold" },
                    { text: props?.encounterNumber || "", style: "fieldValues" },
                  ],
                  alignment: "right",
                  lineHeight: 0.8,
                  border: [false, false, false, false],
                },
              ],
              [
                {
                  text: [
                    { text: "ชื่อ - นามสกุล (Name): ", style: "fieldDataBold" },
                    { text: props?.data?.patient_name || "", style: "fieldValues" },
                  ],
                  lineHeight: 0.8,
                  border: [false, false, false, false],
                },
                {
                  text: [
                    { text: "เพศ (Gender) : ", style: "fieldDataBold" },
                    { text: props?.data?.gender || "", style: "fieldValues" },
                  ],
                  alignment: "right",
                  lineHeight: 0.8,
                  border: [false, false, false, false],
                },
              ],
              [
                {
                  text: [
                    { text: "วันเดือนปีเกิด (Date of Birth): ", style: "fieldDataBold" },
                    { text: props?.hn_data?.birthdate || "", style: "fieldValues" },
                  ],
                  lineHeight: 0.8,
                  border: [false, false, false, true],
                  borderColor: ["#aaa", "#aaa", "#aaa", "#aaa"],
                },
                {
                  text: [
                    { text: "อายุ (Date of Birth): ", style: "fieldDataBold" },
                    { text: props?.hn_data?.full_age || "", style: "fieldValues" },
                  ],
                  alignment: "right",
                  lineHeight: 0.8,
                  border: [false, false, false, true],
                  borderColor: ["#aaa", "#aaa", "#aaa", "#aaa"],
                },
              ],
              [
                {
                  stack: [
                    {
                      text: [
                        { text: "วัน เวลา ที่สั่ง (Order Date/Time) : ", style: "fieldDataBold" },
                        { text: props?.data?.date || "", style: "fieldValues" },
                      ],
                    },
                    {
                      text: [
                        {
                          text: "ผู้ให้บริการ (Provider) : ",
                          style: "fieldDataBold",
                        },
                        {
                          text: `${props?.data?.doctor_name || ""} (${props?.data?.division_name || ""})`,
                          style: "fieldValues",
                        },
                      ],
                    },
                  ],
                  border: [false, false, false, false],
                },
                {
                  image: textToBase64BarcodeBlob(props?.data?.full_access_number || ""),
                  width: 80,
                  height: 35,
                  alignment: "right",
                  border: [false, false, false, false],
                },
              ],
            ],
          },
        },
      ],
    },
    images: {
      ...images,
      ...(oaLogoBase64 && { oaLogo: oaLogoBase64 }),
    },
    content: [
      {
        margin: [10, 0, 15, 0],
        style: "tableExample",
        table: {
          headerRows: 1,
          widths: ["30%", "25%", "*"],
          body: [
            [
              { text: "Specimen", style: "fieldDataBold", margin: [10, 0, 0, 0] },
              { text: "Code", style: "fieldDataBold" },
              { text: "Lab test", style: "fieldDataBold" },
            ],

            ...labOrderList,
          ],
        },
        layout: {
          hLineWidth(i: number, node: any) {
            return 1;
          },
          vLineWidth(i: number, node: any) {
            return i === 0 || i === node.table.widths.length ? 1 : 0;
          },
          hLineColor(i: number, node: any) {
            if (i === node.table.body.length) {
              return "black";
            }

            return i === 0 || i === 1 ? "black" : "#aaa";
          },
          paddingLeft(i: number) {
            return i === 0 ? 0 : 8;
          },
          paddingRight(i: number, node: any) {
            return i === node.table.widths.length - 1 ? 0 : 8;
          },
        },
      },
      {
        margin: [10, 15, 15, 0],
        text: [
          { text: "หมายเหตุ (Remark) : ", style: "fieldDataBold" },
          { text: `${props.data.note || "-"}`, style: "fieldValues" },
        ],
      },
    ],
    footer: function (currentPage: any, pageCount: any) {
      return {
        margin: [17, 68, 0, 0],
        text: [
          {
            text: [{ text: " ผู้พิมพ์ : ", style: "fieldDataBold" }],
            alignment: "right",
          },
        ],

        columns: [
          {
            width: 230,
            text: [
              {
                text: [
                  { text: " ผู้พิมพ์ : ", style: "fieldFooter" },
                  { text: props?.username || "", style: "fieldFooter" },
                ],
                alignment: "right",
              },
            ],
          },

          {
            width: 295,
            text: [
              {
                text: [
                  { text: " วัน เวลาที่พิมพ์:", style: "fieldFooter" },
                  { text: ` ${printDate} `, style: "fieldFooter" },
                ],
                alignment: "right",
              },
            ],
          },
          {
            text: [
              {
                text: [
                  {
                    text: " หน้าที่ " + currentPage.toString() + "/" + pageCount,
                    style: "fieldPrintDate",
                  },
                ],
                alignment: "left",
              },
            ],
          },
        ],
      };
    },
  };
};

export default FormMeLabOrderQueue;
